import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  IconButton,
  Input,
  SkeletonText,
  Spinner,
  Tab,
  TabList,
  Tabs,
  Text,
  Textarea,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import {
  ArrowsClockwise,
  Check,
  CheckCircle,
  FileText,
  Info,
  PaperPlaneRight,
  PencilSimple,
  Sparkle,
  UserSwitch,
} from 'phosphor-react';
import { useFormContext, useWatch } from 'react-hook-form';

import {
  AssuntoDoChat,
  IAdministracaoIA,
  IClausulaDoRascunho,
  IDadosContratoVigente,
  IMensagemDaAdministracao,
  IRascunhoDaAdministracao,
  IResumoDaAdministracao,
  ITextoPronto,
  useSocietario,
} from '../../../../../hooks/useSocietario';
import { useDialog } from '../../../../../providers/dialog';
import { extractApiErrorMessage } from '../../../../../utils/ErrorUtils';
import {
  mesmoTexto,
  pessoasDaAdministracao,
  quemAdministraMudou,
  redacaoForaDoQuadro,
  trechosDaClausula,
  vinculoNaTela,
} from '../administracao';
import type { IWizardFormValues } from '../schema';

interface StepAdministracaoProps {
  contratoId: number | null;
  estadoInicial: IAdministracaoIA | null | undefined;
  vigente: IDadosContratoVigente | null | undefined;
  /** O estado novo (resumo, conversa, aprovação) sobe para a página. */
  onEstado?: (estado: IAdministracaoIA | null) => void;
  /** Texto mexido na tela e não aprovado de novo: a página segura o Continuar e o Gerar. */
  onEdicaoPendente?: (pendente: boolean) => void;
  /** Volta ao passo Sócios, onde se marca quem administra. */
  onMudarQuemAdministra?: () => void;
  /** `clausulas`: "Outras cláusulas" (15/10), alterar ou incluir cláusula do vigente, com textos prontos. */
  assunto?: AssuntoDoChat;
  /** Intervalo da consulta do pedido em andamento (testes usam curto). */
  intervaloDeConsultaMs?: number;
}

type Parte = 'parte_a' | 'parte_b';

const CARTAO = {
  bg: 'white',
  borderWidth: '1px',
  borderColor: 'gray.200',
  borderRadius: 'xl',
  boxShadow: 'sm',
  overflow: 'hidden',
} as const;

// Cada painel rola por dentro: `sticky` não gruda sob o GlassCard do wizard (overflow hidden). A área
// cabe entre o cabeçalho fixo (64px) e a barra fixa do rodapé; ao entrar na etapa, a página rola até ela.
const ALTURA_DOS_PAINEIS = { base: 'auto', lg: 'max(400px, calc(100vh - 260px))' };
const MARGEM_DA_AREA = { scrollMarginTop: 80, scrollMarginBottom: 104 };

const ROTULO_DA_PARTE: Record<Parte, string> = {
  parte_a: 'Parte A — o que o ato altera',
  parte_b: 'Parte B — como fica no contrato',
};

const AJUDA_DA_PARTE: Record<Parte, string> = {
  parte_a: 'Entra nas alterações; o título vai para a Ementa.',
  parte_b: 'Entra na consolidação, no lugar da cláusula do vigente.',
};

const PARTE_CURTA: Record<Parte, string> = { parte_a: 'Parte A', parte_b: 'Parte B' };

// Falhas seguidas da consulta do pedido em andamento antes de parar e oferecer "Tentar de novo".
const MAXIMO_DE_FALHAS_DA_CONSULTA = 5;

const SUGESTOES = [
  'Passa a administrar junto com o atual; cada um assina sozinho.',
  'Deixa a administração por renúncia.',
  'Acima de R$ 50 mil, os dois assinam juntos.',
  'Nomear um administrador não sócio.',
];

const SUGESTOES_DAS_CLAUSULAS = [
  'Altere a cláusula [nº] para dizer que …',
  'Altere o parágrafo [nº] da cláusula [nº]: …',
  'Inclua uma cláusula sobre …',
];

const TEXTOS: Record<AssuntoDoChat, Record<string, string>> = {
  administracao: {
    titulo: 'Administração',
    descricao:
      'Diga na conversa o que muda. A IA redige as cláusulas no estilo do contrato vigente; você lê ao lado como vai ficar, ajusta e aprova.',
    vazio:
      'Conte o que muda na administração: quem entra ou sai, se assina sozinho ou em conjunto, limites de valor.',
    campo: 'O que muda na administração?',
    lendo: 'Lendo a administração do contrato vigente…',
    clausulas: 'Cláusulas sobre a administração',
    nova: 'Entra depois da cláusula de administração e renumera as seguintes',
    aprovada: 'Redação da administração aprovada',
    recomecar: 'A conversa e a redação aprovada da administração serão apagadas.',
    sem_vigente: 'Anexe o contrato vigente para conversar sobre a administração.',
  },
  clausulas: {
    titulo: 'Outras cláusulas',
    descricao:
      'Diga na conversa qual cláusula do contrato vigente muda e como, ou que cláusula nova entra, e marque os textos prontos que o ato inclui. Você lê ao lado como vai ficar, ajusta e aprova.',
    vazio:
      'Diga qual cláusula do contrato vigente muda e como, ou que cláusula nova entra. O número de cada cláusula está na aba "Contrato vigente".',
    campo: 'Que cláusula muda ou entra?',
    lendo: 'Lendo as cláusulas do contrato vigente…',
    clausulas: 'Cláusulas do contrato vigente',
    nova: 'Entra no fim do contrato (antes do foro, se ele é a última) ou depois da cláusula que você pedir',
    aprovada: 'Redação das cláusulas aprovada',
    recomecar: 'A conversa, os textos prontos marcados e a redação aprovada serão apagados.',
    sem_vigente: 'Anexe o contrato vigente para conversar sobre as cláusulas.',
  },
};

// Atalho do caso simples (29/09): só a troca de vínculo no passo Sócios, com os poderes do vigente.
export const PEDIDO_PELO_PASSO_SOCIOS =
  'Redija a alteração da administração conforme o que marquei no passo Sócios (quem passa a ' +
  'administrar, quem deixa e quem continua), mantendo os poderes e a forma de assinar do contrato vigente.';

const hora = (iso?: string) =>
  iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '';

const Rotulo: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text
    fontSize="xs"
    fontWeight="700"
    color="gray.500"
    textTransform="uppercase"
    letterSpacing="wider"
    mb={2}
  >
    {children}
  </Text>
);

const AvatarDaIA: React.FC = () => (
  <Flex
    w="28px"
    h="28px"
    flexShrink={0}
    borderRadius="full"
    bg="blue.50"
    color="blue.500"
    align="center"
    justify="center"
  >
    <Sparkle size={15} weight="fill" />
  </Flex>
);

/** O texto como no documento: parágrafos, rótulo do parágrafo em negrito e listas recuadas. */
const TextoDaClausula: React.FC<{ texto: string }> = ({ texto }) => (
  <Box fontSize="15px" lineHeight="1.75" color="gray.800">
    {trechosDaClausula(texto).map((t, i) =>
      t.tipo === 'item' ? (
        // eslint-disable-next-line react/no-array-index-key
        <Flex key={i} gap={2} pl={6} mb={1}>
          {t.rotulo && (
            <Text as="span" color="gray.400">
              {t.rotulo}
            </Text>
          )}
          <Text as="span">{t.texto}</Text>
        </Flex>
      ) : (
        // eslint-disable-next-line react/no-array-index-key
        <Text key={i} mb={2}>
          {t.rotulo && (
            <Text as="span" fontWeight="700">
              {t.rotulo}{' '}
            </Text>
          )}
          {t.texto}
        </Text>
      ),
    )}
  </Box>
);

interface BlocoDaClausulaProps {
  parte: Parte;
  indice: number;
  clausula: IClausulaDoRascunho;
  editando: boolean;
  /** A IA está redigindo: a resposta troca o texto, e o que fosse editado agora se perderia. */
  bloqueado?: boolean;
  onEditar: () => void;
  onConcluir: () => void;
  onMudar: (campo: 'titulo' | 'texto', valor: string) => void;
  /** Onde entra a cláusula nova (a tela de cada assunto diz). */
  ajudaDaNova: string;
}

/** Uma cláusula do rascunho: leitura como no documento; "Editar" abre o texto. */
const BlocoDaClausula: React.FC<BlocoDaClausulaProps> = ({
  parte,
  indice,
  clausula,
  editando,
  bloqueado = false,
  onEditar,
  onConcluir,
  onMudar,
  ajudaDaNova,
}) => {
  const nome = `cláusula ${indice + 1} da ${PARTE_CURTA[parte]}`;
  // Na Parte B, a que substitui uma do vigente mantém o título dele: o documento não troca.
  const tituloDoVigente = parte === 'parte_b' && clausula.substitui > 0;
  const linhas = clausula.texto.split('\n').length;
  return (
    <Box py={4} borderTopWidth={indice ? '1px' : 0} borderColor="gray.100">
      <Flex align="center" gap={2} mb={2} minH="24px">
        {parte === 'parte_b' &&
          (clausula.substitui ? (
            <Badge colorScheme="blue" variant="subtle" textTransform="none">
              Substitui a cláusula {clausula.substitui}
            </Badge>
          ) : (
            <Tooltip label={ajudaDaNova}>
              <Badge colorScheme="purple" variant="subtle" textTransform="none">
                {clausula.depois_de
                  ? `Cláusula nova · depois da cláusula ${clausula.depois_de}`
                  : 'Cláusula nova'}
              </Badge>
            </Tooltip>
          ))}
        <Box flex="1" />
        {!editando && (
          <Button
            size="xs"
            variant="ghost"
            colorScheme="blue"
            leftIcon={<PencilSimple size={14} />}
            onClick={onEditar}
            isDisabled={bloqueado}
            title={bloqueado ? 'Aguarde a resposta da IA' : undefined}
            aria-label={`Editar ${nome}`}
          >
            Editar
          </Button>
        )}
      </Flex>

      {editando ? (
        <Box>
          {tituloDoVigente ? (
            <Text fontSize="xs" color="gray.500" mb={2}>
              Título do vigente, mantido: {clausula.titulo || '—'}
            </Text>
          ) : (
            <Input
              size="sm"
              mb={2}
              fontWeight="600"
              value={clausula.titulo}
              placeholder="Título da cláusula"
              onChange={e => onMudar('titulo', e.target.value)}
              aria-label={`Título da ${nome}`}
            />
          )}
          <Textarea
            fontSize="sm"
            lineHeight="1.7"
            rows={Math.min(18, Math.max(5, linhas + Math.ceil(clausula.texto.length / 110)))}
            value={clausula.texto}
            onChange={e => onMudar('texto', e.target.value)}
            aria-label={`Texto da ${nome}`}
          />
          <Flex justify="flex-end" mt={2}>
            <Button size="sm" leftIcon={<Check size={14} />} onClick={onConcluir}>
              Concluir
            </Button>
          </Flex>
        </Box>
      ) : (
        <Box>
          {clausula.titulo && (
            <Tooltip
              label="Título do contrato vigente — mantido"
              isDisabled={!tituloDoVigente}
              openDelay={300}
            >
              <Text
                textAlign="center"
                fontWeight="700"
                fontSize="sm"
                letterSpacing="wide"
                textTransform="uppercase"
                color="gray.800"
                mb={3}
              >
                {clausula.titulo}
              </Text>
            </Tooltip>
          )}
          <TextoDaClausula texto={clausula.texto} />
        </Box>
      )}
    </Box>
  );
};

const BolhaDaConversa: React.FC<{ mensagem: IMensagemDaAdministracao }> = ({ mensagem }) => {
  const doOperador = mensagem.papel === 'operador';
  return (
    <Flex justify={doOperador ? 'flex-end' : 'flex-start'} align="flex-end" gap={2} mb={4}>
      {!doOperador && <AvatarDaIA />}
      <Box maxW="85%">
        <Box
          bg={doOperador ? 'blue.500' : 'gray.100'}
          color={doOperador ? 'white' : 'gray.800'}
          px={3}
          py={2}
          borderRadius="2xl"
          borderBottomRightRadius={doOperador ? 'md' : '2xl'}
          borderBottomLeftRadius={doOperador ? '2xl' : 'md'}
        >
          <Text fontSize="sm" lineHeight="1.55" whiteSpace="pre-wrap">
            {mensagem.texto}
          </Text>
        </Box>
        {!!mensagem.falta?.length && (
          <Box
            mt={2}
            bg="orange.50"
            borderWidth="1px"
            borderColor="orange.200"
            borderRadius="lg"
            px={3}
            py={2}
          >
            <Text fontSize="xs" fontWeight="700" color="orange.700" mb={1}>
              A IA precisa saber
            </Text>
            {mensagem.falta.map(f => (
              <Text key={f} fontSize="sm" color="orange.800">
                • {f}
              </Text>
            ))}
          </Box>
        )}
        {mensagem.em && (
          <Text fontSize="10px" color="gray.400" mt={1} textAlign={doOperador ? 'right' : 'left'}>
            {hora(mensagem.em)}
          </Text>
        )}
      </Box>
    </Flex>
  );
};

// Outras cláusulas (15/10): a maioria não tem título próprio (só a que abre o capítulo); o começo do
// texto diz qual é, e o operador abre a cláusula inteira para conferir.
const ClausulaDoVigente: React.FC<{ numero: number; do_que_trata: string; texto?: string }> = ({
  numero,
  do_que_trata: titulo,
  texto,
}) => {
  const [aberta, setAberta] = useState(false);
  const longa = !!texto && (texto.length > 160 || texto.includes('\n'));
  return (
    <Flex
      gap={3}
      py={2}
      borderTopWidth="1px"
      borderColor="gray.100"
      data-testid={`clausula-do-vigente-${numero}`}
    >
      <Text fontSize="sm" fontWeight="700" color="gray.700" flexShrink={0} minW="88px">
        Cláusula {numero}
      </Text>
      <Box minW={0}>
        {titulo && (
          <Text
            fontSize={texto ? 'xs' : 'sm'}
            fontWeight={texto ? '600' : '400'}
            color={texto ? 'gray.500' : 'gray.600'}
          >
            {titulo}
          </Text>
        )}
        {texto && (
          <Text
            fontSize="sm"
            color="gray.700"
            whiteSpace="pre-line"
            // Corta só quando há o botão para abrir: texto curto em coluna estreita saía cortado sem ele.
            noOfLines={longa && !aberta ? 2 : undefined}
          >
            {texto}
          </Text>
        )}
        {longa && (
          <Button
            variant="link"
            size="xs"
            color="blue.600"
            mt={1}
            aria-expanded={aberta}
            aria-label={`${aberta ? 'Recolher' : 'Ver texto completo'} da cláusula ${numero}`}
            onClick={() => setAberta(a => !a)}
          >
            {aberta ? 'Recolher' : 'Ver texto completo'}
          </Button>
        )}
      </Box>
    </Flex>
  );
};

/** Rascunho sem nada a aplicar (sem cláusulas, exclusões nem textos prontos). */
const vazia = (r: IRascunhoDaAdministracao): boolean =>
  !r.parte_a.length &&
  !r.parte_b.length &&
  !(r.excluidas ?? []).length &&
  !(r.textos_prontos ?? []).length;

const ResumoDoVigente: React.FC<{
  resumo: IResumoDaAdministracao | null | undefined;
  lendo: boolean;
  erro: string | null;
  onTentarDeNovo: () => void;
  textos: Record<string, string>;
}> = ({ resumo, lendo, erro, onTentarDeNovo, textos }) => {
  if (lendo) {
    return (
      <Box>
        <Flex align="center" gap={2} color="gray.500" fontSize="sm" mb={4}>
          <Spinner size="xs" /> {textos.lendo}
        </Flex>
        <SkeletonText noOfLines={7} spacing="3" skeletonHeight="3" />
      </Box>
    );
  }
  if (erro) {
    return (
      <Flex direction="column" gap={2} align="flex-start">
        <Text fontSize="sm" color="red.600">
          {erro}
        </Text>
        <Button size="sm" onClick={onTentarDeNovo}>
          Tentar de novo
        </Button>
      </Flex>
    );
  }
  if (!resumo) return null;
  return (
    <Box data-testid="resumo-do-vigente">
      <Text fontSize="md" lineHeight="1.6" color="gray.800" mb={5}>
        {resumo.resumo}
      </Text>

      {resumo.administradores.length > 0 && (
        <Box mb={5}>
          <Rotulo>Quem administra hoje</Rotulo>
          {resumo.administradores.map(a => (
            <Box key={a.nome} py={2} borderTopWidth="1px" borderColor="gray.100">
              <Text fontSize="sm" fontWeight="600">
                {a.nome}
                {!a.socio && (
                  <Text as="span" fontWeight="400" color="gray.500">
                    {' '}
                    (não sócio)
                  </Text>
                )}
              </Text>
              {a.como_assina && (
                <Text fontSize="sm" color="gray.600">
                  {a.como_assina}
                </Text>
              )}
            </Box>
          ))}
        </Box>
      )}

      {!!resumo.pontos?.length && (
        <Box mb={5}>
          <Rotulo>O que o contrato diz</Rotulo>
          {resumo.pontos.map(p => (
            <Flex key={p} gap={2} mb={2} fontSize="sm" lineHeight="1.6" color="gray.700">
              <Text as="span" color="blue.400">
                •
              </Text>
              <Text as="span">{p}</Text>
            </Flex>
          ))}
        </Box>
      )}

      {resumo.clausulas.length > 0 && (
        <Box>
          <Rotulo>{textos.clausulas}</Rotulo>
          {resumo.clausulas.map(c => (
            <ClausulaDoVigente key={c.numero} {...c} />
          ))}
        </Box>
      )}
    </Box>
  );
};

/**
 * Administração por chat (29/09): o operador diz o que muda, a IA redige as cláusulas no estilo do
 * vigente e o operador aprova. Sem trava: o texto aprovado vai ao contrato como está.
 * Tela: conversa à esquerda, documento à direita (redação proposta ou o resumo do vigente).
 */
export const StepAdministracao: React.FC<StepAdministracaoProps> = ({
  contratoId,
  estadoInicial,
  vigente,
  onEstado,
  onEdicaoPendente,
  onMudarQuemAdministra,
  assunto = 'administracao',
  intervaloDeConsultaMs = 2500,
}) => {
  const {
    estadoDaAdministracao,
    resumoDaAdministracao,
    mensagemDaAdministracao,
    aprovarAdministracao,
    recomecarAdministracao,
    textosProntos,
  } = useSocietario();
  const ehClausulas = assunto === 'clausulas';
  const textos = TEXTOS[assunto];
  const toast = useToast();
  const showDialog = useDialog();
  const { control } = useFormContext<IWizardFormValues>();
  const socios = useWatch({ control, name: 'socios' });
  const pessoas = useMemo(() => pessoasDaAdministracao(socios, vigente), [socios, vigente]);

  const [estado, setEstado] = useState<IAdministracaoIA | null>(estadoInicial ?? null);
  const [erroDoResumo, setErroDoResumo] = useState<string | null>(null);
  const [pedindoResumo, setPedindoResumo] = useState(false);
  const [ciclo, setCiclo] = useState(0);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [aprovando, setAprovando] = useState(false);
  const [edicao, setEdicao] = useState<IRascunhoDaAdministracao | null>(
    estadoInicial?.rascunho ?? null,
  );
  const [editando, setEditando] = useState<string | null>(null);
  // 0 = redação proposta, 1 = contrato vigente. Sem rascunho, abre no vigente.
  const [aba, setAba] = useState(estadoInicial?.rascunho ? 0 : 1);
  const conversaRef = useRef<HTMLDivElement | null>(null);
  const campoRef = useRef<HTMLTextAreaElement | null>(null);
  const areaRef = useRef<HTMLDivElement | null>(null);

  // Depois da rolagem da troca de etapa: conversa e documento inteiros na tela, com a entrada do chat
  // acima da barra fixa (29/09: com a página no topo, a barra cobria a entrada).
  useEffect(() => {
    const espera = window.setTimeout(() => {
      const area = areaRef.current;
      if (area && typeof area.scrollIntoView === 'function' && window.innerWidth >= 992) {
        area.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 400);
    return () => window.clearTimeout(espera);
  }, []);

  const estadoRef = useRef<IAdministracaoIA | null>(estado);
  estadoRef.current = estado;
  // Conversas recomeçadas aqui: resposta atrasada delas não volta para a tela (2ª review de 29/09:
  // um GET que saiu antes do "Recomeçar" voltava depois e travava a tela na conversa velha).
  const conversasDescartadas = useRef<Set<string>>(new Set());
  const atualizar = (novo: IAdministracaoIA | null) => {
    const atual = estadoRef.current;
    if (novo?.conversa && conversasDescartadas.current.has(novo.conversa)) return;
    // Da mesma conversa, resposta mais velha que a da tela (um GET que saiu antes do POST) fica de
    // fora: o back sobe `versao` a cada gravação. De outra conversa (recomeçada em outra aba), vale.
    const mesmaConversa = (novo?.conversa ?? '') === (atual?.conversa ?? '');
    if (novo && atual && mesmaConversa && (novo.versao ?? 0) < (atual.versao ?? 0)) return;
    estadoRef.current = novo;
    setEstado(novo);
    onEstado?.(novo);
  };

  // A página pode ter um estado mais novo que esta etapa: a resposta da IA que chegou com a etapa
  // fechada (review de 29/09: voltar antes da resposta deixava a tela velha, num beco). Só o mais novo.
  useEffect(() => {
    const local = estadoRef.current;
    if (!estadoInicial || estadoInicial === local) return;
    const versaoMaior = (estadoInicial.versao ?? 0) > (local?.versao ?? 0);
    const conversaMaior = (estadoInicial.historico?.length ?? 0) > (local?.historico?.length ?? 0);
    const aprovouDepois = (estadoInicial.aprovado?.em ?? '') > (local?.aprovado?.em ?? '');
    if (!local || versaoMaior || conversaMaior || aprovouDepois) {
      estadoRef.current = estadoInicial;
      setEstado(estadoInicial);
    }
  }, [estadoInicial]);

  const lerResumo = async () => {
    if (!contratoId) return;
    setPedindoResumo(true);
    setErroDoResumo(null);
    try {
      atualizar(await resumoDaAdministracao(contratoId, assunto));
    } catch (err) {
      setErroDoResumo(extractApiErrorMessage(err, 'Não foi possível ler o contrato vigente.'));
    } finally {
      setPedindoResumo(false);
    }
  };

  useEffect(() => {
    if (contratoId && !estado?.resumo && !estado?.pendente?.resumo) lerResumo();
    // So' na entrada do passo: o resumo fica gravado no contrato.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contratoId]);

  // A IA roda em segundo plano (o ALB de prod corta pedido parado em 60 s): enquanto houver pedido
  // pendente, a tela consulta o estado até ele terminar — inclusive se a etapa abrir no meio.
  const aguardandoResumo = !!estado?.pendente?.resumo;
  const aguardandoMensagem = !!estado?.pendente?.mensagem;
  // Consulta que falha seguido (403, 404, 5xx) para de tentar e oferece "Tentar de novo": antes
  // tentava para sempre, com a caixa travada e sem aviso (2ª review de 29/09).
  const [falhasDaConsulta, setFalhasDaConsulta] = useState(0);
  const consultaParou = falhasDaConsulta >= MAXIMO_DE_FALHAS_DA_CONSULTA;
  useEffect(() => {
    if (!contratoId || consultaParou || !(aguardandoResumo || aguardandoMensagem)) {
      return undefined;
    }
    let vivo = true;
    const espera = window.setTimeout(async () => {
      try {
        const novo = await estadoDaAdministracao(contratoId, assunto);
        if (vivo) {
          atualizar(novo);
          setFalhasDaConsulta(0);
        }
      } catch {
        if (vivo) setFalhasDaConsulta(f => f + 1);
      }
      if (vivo) setCiclo(c => c + 1);
    }, intervaloDeConsultaMs);
    return () => {
      vivo = false;
      window.clearTimeout(espera);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contratoId, aguardandoResumo, aguardandoMensagem, ciclo, consultaParou]);

  // Pedido que falhou no segundo plano: avisa uma vez, e o texto do operador volta para a caixa.
  const erroVisto = useRef<string | null>(estadoInicial?.erro?.em ?? null);
  useEffect(() => {
    const erro = estado?.erro;
    if (!erro || (erro.em ?? '') === erroVisto.current) return;
    erroVisto.current = erro.em ?? '';
    if (erro.tipo === 'mensagem') {
      toast({ status: 'error', title: 'A IA não respondeu', description: erro.detalhe });
      if (erro.mensagem) setTexto(atual => atual || erro.mensagem || '');
    } else {
      setErroDoResumo(erro.detalhe);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado?.erro]);

  // Rascunho NOVO da IA (por valor): a edição recomeça dele e a tela mostra a redação. Mesmo texto (a
  // IA só respondeu, ou objeto novo com o mesmo conteúdo) mantém o que está na tela, editado ou não
  // (review de 29/09: uma pergunta à IA apagava a edição sem aviso).
  const rascunhoDoServidor = useRef<IRascunhoDaAdministracao | null>(
    estadoInicial?.rascunho ?? null,
  );
  useEffect(() => {
    const novo = estado?.rascunho ?? null;
    const anterior = rascunhoDoServidor.current;
    if ((!novo && !anterior) || mesmoTexto(novo, anterior)) return;
    rascunhoDoServidor.current = novo;
    setEdicao(novo);
    setEditando(null);
    if (novo) setAba(0);
  }, [estado?.rascunho]);

  const historico = estado?.historico ?? [];
  // POST no ar ou pedido rodando em segundo plano.
  const aguardandoIA = enviando || aguardandoMensagem;
  useEffect(() => {
    const caixa = conversaRef.current;
    if (caixa && typeof caixa.scrollTo === 'function') {
      caixa.scrollTo({ top: caixa.scrollHeight });
    }
  }, [historico.length, aguardandoIA]);

  const enviar = async (pedidoPronto?: string) => {
    const mensagem = (pedidoPronto ?? texto).trim();
    if (!contratoId || !mensagem || aguardandoIA) return;
    setEnviando(true);
    try {
      // Vai o texto da tela: a IA ajusta a partir do que o operador editou.
      atualizar(
        await mensagemDaAdministracao(contratoId, { mensagem, pessoas, rascunho: edicao }, assunto),
      );
      // A caixa esvazia também quando o reenvio era o texto que tinha voltado para ela.
      if (!pedidoPronto || texto.trim() === mensagem) setTexto('');
    } catch (err) {
      toast({
        status: 'error',
        title: 'A IA não respondeu',
        description: extractApiErrorMessage(err, 'Tente de novo em instantes.'),
      });
    } finally {
      setEnviando(false);
    }
  };

  const aprovar = async () => {
    if (!contratoId || !edicao) return;
    setAprovando(true);
    try {
      // Com quem administra agora: se o passo Sócios mudar depois, a aprovação deixa de valer.
      atualizar(
        await aprovarAdministracao(contratoId, edicao, pessoas, estado?.vigente ?? null, assunto),
      );
      setEditando(null);
      toast({ status: 'success', title: textos.aprovada });
    } catch (err) {
      toast({ status: 'error', title: 'Não aprovou', description: extractApiErrorMessage(err) });
    } finally {
      setAprovando(false);
    }
  };

  const recomecar = () =>
    showDialog({
      status: 'warning',
      title: 'Recomeçar a conversa?',
      description: textos.recomecar,
      confirmLabel: 'Recomeçar',
      cancelLabel: 'Cancelar',
      async onConfirm() {
        if (!contratoId) return;
        const conversaVelha = estadoRef.current?.conversa;
        try {
          await recomecarAdministracao(contratoId, assunto);
        } catch (err) {
          // O diálogo não trata a falha: sem isto, nada mudava e ninguém avisava.
          toast({
            status: 'error',
            title: 'Não recomeçou',
            description: extractApiErrorMessage(err),
          });
          return;
        }
        if (conversaVelha) conversasDescartadas.current.add(conversaVelha);
        atualizar(null);
        // Texto pronto marcado só na tela (sem rascunho no servidor) também sai.
        rascunhoDoServidor.current = null;
        setEdicao(null);
        setAba(1);
        await lerResumo();
      },
    });

  // Textos prontos (15/10): o back manda o texto com a concordância deste contrato; marcar entra no
  // rascunho da tela e vale depois de aprovar, como a redação. A IA não os vê nem reescreve.
  const [catalogo, setCatalogo] = useState<ITextoPronto[] | null>(null);
  useEffect(() => {
    if (!ehClausulas || !contratoId) return;
    textosProntos(contratoId)
      .then(setCatalogo)
      .catch(() => setCatalogo([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ehClausulas, contratoId]);
  const marcados = edicao?.textos_prontos ?? [];
  const marcarTextoPronto = (chave: string, marcado: boolean) => {
    setEdicao(atual => {
      const base = atual ?? { parte_a: [], parte_b: [], o_que_mudou: '' };
      const lista = (base.textos_prontos ?? []).filter(c => c !== chave);
      const novo = { ...base, textos_prontos: marcado ? [...lista, chave] : lista };
      // Desmarcou o último sem nada aprovado: sem redação (o back recusa aprovar rascunho vazio).
      return vazia(novo) && !estadoRef.current?.aprovado ? null : novo;
    });
    // O texto marcado aparece na redação: a tela vai para ela.
    setAba(0);
  };
  const redacaoVazia = !!edicao && vazia(edicao);

  const mudarClausula = (parte: Parte, i: number, campo: 'titulo' | 'texto', valor: string) =>
    setEdicao(atual =>
      atual
        ? {
            ...atual,
            [parte]: atual[parte].map((c, j) => (j === i ? { ...c, [campo]: valor } : c)),
          }
        : atual,
    );

  const aprovado = estado?.aprovado ?? null;
  const edicaoJaAprovada = mesmoTexto(edicao, aprovado);
  const edicaoPendente = !!aprovado && !!edicao && !edicaoJaAprovada;
  const mudouQuemAdministra = !ehClausulas && quemAdministraMudou(estado, pessoas);
  const foraDoQuadro = !ehClausulas && redacaoForaDoQuadro(estado, pessoas);
  const aprovadaComoEsta = edicaoJaAprovada && !mudouQuemAdministra;
  useEffect(() => {
    onEdicaoPendente?.(edicaoPendente);
    // So' quando muda: a pagina guarda o ultimo valor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edicaoPendente]);
  // Fora da etapa a edição não aprovada se perde: a página não segura mais por ela.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => onEdicaoPendente?.(false), []);

  if (!contratoId) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <AlertDescription>{textos.sem_vigente}</AlertDescription>
      </Alert>
    );
  }

  const estadoDaAprovacao = (() => {
    if (!edicao) return null;
    if (aguardandoIA) {
      return (
        <Flex align="center" gap={2} color="gray.600" fontSize="sm">
          <Spinner size="xs" /> Aguardando a resposta da IA
        </Flex>
      );
    }
    if (aprovadaComoEsta) {
      return (
        <Flex align="center" gap={1} color="green.600" fontSize="sm" fontWeight="600">
          <CheckCircle size={16} weight="fill" /> Aprovada às {hora(aprovado?.em)}
        </Flex>
      );
    }
    if (foraDoQuadro) {
      return <Badge colorScheme="orange">Redação feita com outro quadro</Badge>;
    }
    if (edicaoJaAprovada && mudouQuemAdministra) {
      return <Badge colorScheme="orange">Quem administra mudou depois da aprovação</Badge>;
    }
    if (aprovado) return <Badge colorScheme="orange">Alterada depois da aprovação</Badge>;
    return (
      <Text fontSize="xs" color="gray.600">
        Ainda não aprovada: aprove para continuar
      </Text>
    );
  })();

  const conversa = (
    <Flex
      {...CARTAO}
      direction="column"
      h={ALTURA_DOS_PAINEIS}
      minH={{ base: '560px', lg: 0 }}
      data-testid="painel-da-conversa"
    >
      <Flex px={4} py={3} borderBottomWidth="1px" borderColor="gray.100" align="center" gap={3}>
        <AvatarDaIA />
        <Box flex="1" minW={0}>
          <Text fontSize="sm" fontWeight="700">
            Conversa com a IA
          </Text>
          <Text fontSize="xs" color="gray.500">
            No estilo do contrato vigente
          </Text>
        </Box>
        {(historico.length > 0 || aprovado) && (
          <Tooltip label="Apaga a conversa e a redação aprovada" openDelay={300}>
            <Button
              size="xs"
              variant="ghost"
              leftIcon={<ArrowsClockwise size={14} />}
              onClick={recomecar}
            >
              Recomeçar conversa
            </Button>
          </Tooltip>
        )}
      </Flex>

      {ehClausulas ? (
        <Box px={4} py={3} bg="gray.50" borderBottomWidth="1px" borderColor="gray.100">
          <Text
            fontSize="xs"
            fontWeight="700"
            color="gray.500"
            textTransform="uppercase"
            letterSpacing="wider"
            mb={1}
          >
            Textos prontos
          </Text>
          {(catalogo === null || catalogo.length === 0) && (
            <Text fontSize="xs" color="gray.500">
              {catalogo === null ? 'Carregando…' : 'Não foi possível carregar os textos prontos.'}
            </Text>
          )}
          {(catalogo ?? []).map(t => (
            <Checkbox
              key={t.chave}
              display="flex"
              size="sm"
              py="2px"
              isChecked={marcados.includes(t.chave)}
              isDisabled={aguardandoIA}
              onChange={e => marcarTextoPronto(t.chave, e.target.checked)}
            >
              <Text as="span" fontSize="sm">
                {t.rotulo}
              </Text>
              <Text as="span" fontSize="xs" color="gray.500">
                {' '}
                — {t.onde}
              </Text>
            </Checkbox>
          ))}
        </Box>
      ) : (
        <Box px={4} py={3} bg="gray.50" borderBottomWidth="1px" borderColor="gray.100">
          <Flex justify="space-between" align="center" mb={1} gap={2}>
            <Text
              fontSize="xs"
              fontWeight="700"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wider"
            >
              Quem administra depois do ato
            </Text>
            <Button
              size="xs"
              variant="link"
              colorScheme="blue"
              leftIcon={<UserSwitch size={14} />}
              onClick={onMudarQuemAdministra}
            >
              Mudar quem administra
            </Button>
          </Flex>
          <Box maxH="128px" overflowY="auto">
            {pessoas.map(p => (
              <Flex key={p.nome} justify="space-between" align="center" py="3px" gap={2}>
                <Text fontSize="sm" noOfLines={1} title={p.nome}>
                  {p.nome}
                  {!p.socio && (
                    <Text as="span" color="gray.500">
                      {' '}
                      (não sócio)
                    </Text>
                  )}
                </Text>
                <Badge
                  colorScheme={p.administra_depois ? 'green' : 'gray'}
                  variant={p.administra_depois !== p.administra_hoje ? 'solid' : 'subtle'}
                  flexShrink={0}
                >
                  {vinculoNaTela(p)}
                </Badge>
              </Flex>
            ))}
          </Box>
        </Box>
      )}

      <Box
        ref={conversaRef}
        flex="1"
        minH={0}
        overflowY="auto"
        px={4}
        py={4}
        data-testid="conversa-administracao"
      >
        {historico.length === 0 && !aguardandoIA && (
          <Flex direction="column" align="center" textAlign="center" gap={3} py={4}>
            <Text fontSize="sm" color="gray.500" maxW="320px">
              {textos.vazio}
            </Text>
            {!ehClausulas && (
              <Box>
                <Button
                  size="sm"
                  colorScheme="blue"
                  leftIcon={<Sparkle size={14} weight="fill" />}
                  onClick={() => enviar(PEDIDO_PELO_PASSO_SOCIOS)}
                >
                  Redigir pelo que marquei em Sócios
                </Button>
                <Text fontSize="xs" color="gray.400" mt={1}>
                  Mantém os poderes e a forma de assinar do vigente
                </Text>
              </Box>
            )}
            <Text fontSize="xs" color="gray.500" mt={1}>
              {ehClausulas ? 'Comece por um destes pedidos:' : 'Ou comece por um destes pedidos:'}
            </Text>
            <Flex gap={2} flexWrap="wrap" justify="center">
              {(ehClausulas ? SUGESTOES_DAS_CLAUSULAS : SUGESTOES).map(s => (
                <Button
                  key={s}
                  size="xs"
                  variant="outline"
                  borderRadius="full"
                  fontWeight="500"
                  whiteSpace="normal"
                  h="auto"
                  py={1}
                  onClick={() => {
                    setTexto(s);
                    campoRef.current?.focus();
                  }}
                >
                  {s}
                </Button>
              ))}
            </Flex>
          </Flex>
        )}
        {historico.map((m, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <BolhaDaConversa key={i} mensagem={m} />
        ))}
        {estado?.pendente?.mensagem && (
          <BolhaDaConversa
            mensagem={{ papel: 'operador', texto: estado.pendente.mensagem.mensagem }}
          />
        )}
        {aguardandoIA && (
          <Flex align="flex-end" gap={2} mb={4}>
            <AvatarDaIA />
            <Flex bg="gray.100" px={3} py={2} borderRadius="2xl" align="center" gap={2}>
              <Spinner size="xs" color="gray.500" />
              <Text fontSize="sm" color="gray.600">
                A IA está redigindo… costuma levar menos de 1 minuto
              </Text>
            </Flex>
          </Flex>
        )}
        {consultaParou && (aguardandoMensagem || aguardandoResumo) && (
          <Alert status="warning" borderRadius="lg" mb={3} alignItems="flex-start">
            <AlertIcon />
            <Box>
              <AlertDescription fontSize="sm">
                Não consegui acompanhar o pedido: o servidor não respondeu.
              </AlertDescription>
              <Button mt={2} size="xs" onClick={() => setFalhasDaConsulta(0)}>
                Tentar de novo
              </Button>
            </Box>
          </Alert>
        )}
        {/* O pedido que falhou fica à vista até o próximo (também depois de recarregar a página). */}
        {!aguardandoIA && estado?.erro?.tipo === 'mensagem' && (
          <Alert status="error" borderRadius="lg" mb={3} alignItems="flex-start">
            <AlertIcon />
            <Box>
              <AlertDescription fontSize="sm">
                A IA não respondeu
                {estado.erro.mensagem ? ` "${estado.erro.mensagem}"` : ' o último pedido'}:{' '}
                {estado.erro.detalhe}
              </AlertDescription>
              {estado.erro.mensagem && (
                <Button mt={2} size="xs" onClick={() => enviar(estado.erro?.mensagem)}>
                  Mandar de novo
                </Button>
              )}
            </Box>
          </Alert>
        )}
      </Box>

      <Box px={3} pt={2} pb={3} borderTopWidth="1px" borderColor="gray.100">
        <Flex
          align="flex-end"
          gap={2}
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="lg"
          pl={3}
          pr={2}
          py={2}
          bg="white"
          _focusWithin={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px #4299E1' }}
        >
          <Textarea
            ref={campoRef}
            variant="unstyled"
            resize="none"
            fontSize="sm"
            rows={Math.min(5, Math.max(2, texto.split('\n').length))}
            value={texto}
            placeholder={textos.campo}
            aria-label="Mensagem para a IA"
            onChange={e => setTexto(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                enviar();
              }
            }}
            isDisabled={aguardandoIA}
          />
          <IconButton
            aria-label="Enviar"
            icon={<PaperPlaneRight size={18} weight="fill" />}
            colorScheme="blue"
            size="sm"
            onClick={() => enviar()}
            isLoading={aguardandoIA}
            isDisabled={!texto.trim()}
          />
        </Flex>
        <Text fontSize="xs" color="gray.400" mt={1} px={1}>
          Enter envia · Shift+Enter quebra a linha
        </Text>
      </Box>
    </Flex>
  );

  const redacao = !edicao ? (
    <Flex direction="column" align="center" textAlign="center" color="gray.500" gap={3} py={12}>
      <FileText size={36} />
      <Text fontSize="sm" maxW="340px">
        A redação aparece aqui depois do primeiro pedido na conversa. Você lê como vai ficar no
        contrato, edita o que quiser e aprova.
      </Text>
    </Flex>
  ) : (
    <Box data-testid="redacao-administracao">
      {(foraDoQuadro || mudouQuemAdministra) && (
        <Alert status="warning" borderRadius="lg" mb={4} alignItems="flex-start">
          <AlertIcon />
          <Box>
            <AlertDescription fontSize="sm">
              Quem administra (ou a qualificação de quem passa a administrar) mudou no passo Sócios
              depois desta redação. Peça a redação de novo para ela acompanhar o quadro.
            </AlertDescription>
            <Button
              mt={2}
              size="xs"
              colorScheme="orange"
              leftIcon={<Sparkle size={12} weight="fill" />}
              onClick={() => enviar(PEDIDO_PELO_PASSO_SOCIOS)}
              isLoading={aguardandoIA}
            >
              Redigir de novo pelo que marquei em Sócios
            </Button>
          </Box>
        </Alert>
      )}
      {edicao.o_que_mudou && (
        <Flex gap={3} bg="blue.50" borderRadius="lg" p={3} mb={5}>
          <Box color="blue.500" pt="2px" flexShrink={0}>
            <Info size={18} weight="fill" />
          </Box>
          <Text fontSize="sm" color="gray.700" lineHeight="1.6">
            {edicao.o_que_mudou}
          </Text>
        </Flex>
      )}
      {(['parte_a', 'parte_b'] as Parte[]).map(parte =>
        edicao[parte].length > 0 ? (
          <Box key={parte} mb={6}>
            <Box pb={1} borderBottomWidth="2px" borderColor="gray.100">
              <Text
                fontSize="xs"
                fontWeight="700"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                {ROTULO_DA_PARTE[parte]}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {AJUDA_DA_PARTE[parte]}
              </Text>
            </Box>
            {edicao[parte].map((c, i) => {
              const chave = `${parte}-${i}`;
              return (
                <BlocoDaClausula
                  key={chave}
                  parte={parte}
                  indice={i}
                  clausula={c}
                  editando={editando === chave}
                  bloqueado={aguardandoIA}
                  onEditar={() => setEditando(chave)}
                  onConcluir={() => setEditando(null)}
                  onMudar={(campo, valor) => mudarClausula(parte, i, campo, valor)}
                  ajudaDaNova={textos.nova}
                />
              );
            })}
          </Box>
        ) : null,
      )}
      {!!edicao.excluidas?.length && (
        <Box mb={6} data-testid="clausulas-excluidas">
          <Box pb={1} borderBottomWidth="2px" borderColor="gray.100">
            <Text
              fontSize="xs"
              fontWeight="700"
              color="gray.600"
              textTransform="uppercase"
              letterSpacing="wider"
            >
              Saem do contrato
            </Text>
            <Text fontSize="xs" color="gray.500">
              Cláusulas do vigente que a consolidação não traz mais; as seguintes são renumeradas.
            </Text>
          </Box>
          {edicao.excluidas.map(n => {
            const doVigente = estado?.resumo?.clausulas.find(c => c.numero === n);
            return (
              <Flex key={n} gap={3} py={3} borderTopWidth="1px" borderColor="gray.100">
                <Badge colorScheme="red" variant="subtle" textTransform="none" flexShrink={0}>
                  Exclui a cláusula {n}
                </Badge>
                <Text fontSize="sm" color="gray.600" noOfLines={2}>
                  {doVigente?.do_que_trata || doVigente?.texto || ''}
                </Text>
              </Flex>
            );
          })}
        </Box>
      )}
      {(catalogo ?? [])
        .filter(t => marcados.includes(t.chave))
        .map(t => (
          <Box key={t.chave} mb={6} data-testid={`texto-pronto-${t.chave}`}>
            <Box pb={1} borderBottomWidth="2px" borderColor="gray.100">
              <Flex align="center" gap={2}>
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Texto pronto — {t.rotulo}
                </Text>
                <Badge colorScheme="teal" variant="subtle" textTransform="none">
                  Texto fixo
                </Badge>
              </Flex>
              <Text fontSize="xs" color="gray.500">
                {t.onde}
              </Text>
            </Box>
            {[t.parte_a, t.parte_b].map(
              (bloco, i) =>
                bloco && (
                  // eslint-disable-next-line react/no-array-index-key
                  <Box key={i} py={3}>
                    <Text fontSize="xs" color="gray.500" mb={1}>
                      {i === 0 ? PARTE_CURTA.parte_a : PARTE_CURTA.parte_b}
                    </Text>
                    {bloco.titulo && (
                      <Text
                        textAlign="center"
                        fontWeight="700"
                        fontSize="sm"
                        textTransform="uppercase"
                        mb={2}
                      >
                        {bloco.titulo}
                      </Text>
                    )}
                    <TextoDaClausula texto={bloco.texto} />
                  </Box>
                ),
            )}
          </Box>
        ))}
      <Text fontSize="xs" color="gray.500">
        Edite o que precisar antes de aprovar. O texto aprovado vai ao contrato como está.
      </Text>
    </Box>
  );

  const documento = (
    <Flex {...CARTAO} direction="column" h={ALTURA_DOS_PAINEIS} data-testid="painel-do-documento">
      <Flex
        px={4}
        py={2}
        minH="56px"
        borderBottomWidth="1px"
        borderColor="gray.100"
        align="center"
        gap={3}
        flexWrap="wrap"
      >
        <Tabs index={aba} onChange={setAba} variant="soft-rounded" colorScheme="blue" size="sm">
          <TabList gap={1}>
            <Tab fontSize="sm">Redação proposta</Tab>
            <Tab fontSize="sm">Contrato vigente</Tab>
          </TabList>
        </Tabs>
      </Flex>
      <Box flex="1" minH={0} overflowY="auto" px={{ base: 4, md: 6 }} py={5}>
        {aba === 0 ? (
          redacao
        ) : (
          <ResumoDoVigente
            resumo={estado?.resumo}
            lendo={pedindoResumo || aguardandoResumo}
            erro={erroDoResumo}
            onTentarDeNovo={lerResumo}
            textos={textos}
          />
        )}
      </Box>
      {/* Aprovar fica sempre à vista, no rodapé do painel (o texto rola por dentro). */}
      {edicao && (
        <Flex
          px={4}
          py={3}
          borderTopWidth="1px"
          borderColor="gray.100"
          bg="gray.50"
          align="center"
          gap={3}
        >
          {estadoDaAprovacao}
          <Box flex="1" />
          {redacaoVazia ? (
            // Desmarcou o último texto pronto de uma redação aprovada: não há o que aprovar, e o
            // aprovado antigo não pode valer calado (review de 15/10).
            <Text fontSize="sm" color="gray.600" textAlign="right">
              Nada para aprovar. Para tirar a redação aprovada, use “Recomeçar conversa”.
            </Text>
          ) : (
            <Button
              colorScheme="green"
              size="sm"
              variant={aprovadaComoEsta ? 'outline' : 'solid'}
              leftIcon={aprovadaComoEsta ? <Check size={14} weight="bold" /> : undefined}
              onClick={aprovar}
              isLoading={aprovando}
              isDisabled={aprovadaComoEsta || aguardandoIA || foraDoQuadro}
              _disabled={{ opacity: 1, cursor: 'default' }}
            >
              {aprovadaComoEsta ? 'Redação aprovada' : 'Usar esta redação'}
            </Button>
          )}
        </Flex>
      )}
    </Flex>
  );

  return (
    <Box ref={areaRef} style={MARGEM_DA_AREA}>
      <Box mb={4}>
        <Text fontSize="lg" fontWeight="bold">
          {textos.titulo}
        </Text>
        <Text fontSize="sm" color="gray.600">
          {textos.descricao}
        </Text>
      </Box>
      <Grid templateColumns={{ base: '1fr', lg: 'minmax(340px, 5fr) 7fr' }} gap={4}>
        {conversa}
        {documento}
      </Grid>
    </Box>
  );
};
